import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { dataProvider } from '@/lib/dataProvider';

const CoursesPage: React.FC = () => {
  const { t } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState<string>('Tất cả');
  const [selectedTrainingType, setSelectedTrainingType] = useState<string>('Tất cả');

  // Get data from data provider
  const courses = dataProvider.getCourses();
  const allCategories = dataProvider.getCategories();
  
  // Get unique training types from courses
  const trainingTypes = ['Tất cả', ...Array.from(new Set(courses.map(course => course.trainingType)))];
  
  // Define category groups
  const categoryGroups = {
    'KIẾN THỨC CHUNG': ['Kiến thức chung'],
    'CHUYÊN MÔN NGHIỆP VỤ': ['Cân bằng trọng tải', 'An ninh & An toàn', 'Hệ thống IT', 'Thiết bị & Kỹ thuật'],
    'ĐÀO TẠO KHÁC': []
  };
  
  // Create main categories for filtering
  const mainCategories = ['Tất cả', 'KIẾN THỨC CHUNG', 'CHUYÊN MÔN NGHIỆP VỤ', 'ĐÀO TẠO KHÁC'];

  const filteredCourses = courses.filter(course => {
    let categoryMatch = selectedCategory === 'Tất cả';
    
    if (!categoryMatch) {
      // Check if course belongs to selected main category group
      if (selectedCategory === 'KIẾN THỨC CHUNG') {
        categoryMatch = categoryGroups['KIẾN THỨC CHUNG'].includes(course.category);
      } else if (selectedCategory === 'CHUYÊN MÔN NGHIỆP VỤ') {
        categoryMatch = categoryGroups['CHUYÊN MÔN NGHIỆP VỤ'].includes(course.category);
      } else if (selectedCategory === 'ĐÀO TẠO KHÁC') {
        // ĐÀO TẠO KHÁC includes categories not in other groups
        const allDefinedCategories = [
          ...categoryGroups['KIẾN THỨC CHUNG'],
          ...categoryGroups['CHUYÊN MÔN NGHIỆP VỤ']
        ];
        categoryMatch = !allDefinedCategories.includes(course.category);
      }
    }
    
    const trainingTypeMatch = selectedTrainingType === 'Tất cả' || course.trainingType === selectedTrainingType;
    return categoryMatch && trainingTypeMatch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Đã xuất bản': return 'bg-green-100 text-green-800';
      case 'Nháp': return 'bg-yellow-100 text-yellow-800';
      case 'Đã lưu trữ': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTrainingTypeColor = (trainingType: string) => {
    switch (trainingType) {
      case 'BAN ĐẦU': return 'bg-blue-100 text-blue-800';
      case 'ĐỊNH KỲ': return 'bg-green-100 text-green-800';
      case 'KHÁC': return 'bg-purple-100 text-purple-800';
      case 'BỔ SUNG': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quản lý Khóa học Đào tạo</h1>
          <p className="text-gray-600 mt-1">Quản lý và theo dõi các khóa học đào tạo trong tổ chức</p>
        </div>
        <Link
          to="/lms/courses/new"
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          <svg className="w-4 h-4 inline mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Tạo khóa học mới
        </Link>
      </div>
      
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Tổng khóa học</p>
              <p className="text-2xl font-semibold text-gray-900">{courses.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Đã xuất bản</p>
              <p className="text-2xl font-semibold text-gray-900">{courses.filter(c => c.status === 'Đã xuất bản').length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-orange-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Có chứng chỉ</p>
              <p className="text-2xl font-semibold text-gray-900">{courses.filter(c => c.certification).length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-purple-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Danh mục</p>
              <p className="text-2xl font-semibold text-gray-900">{mainCategories.length - 1}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 border">
          <div className="flex items-center">
            <div className="bg-indigo-100 p-3 rounded-lg">
              <svg className="h-6 w-6 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Nháp</p>
              <p className="text-2xl font-semibold text-gray-900">{courses.filter(c => c.status === 'Nháp').length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Lọc theo danh mục</h3>
            <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg">
              {mainCategories.map((category) => {
                let count = 0;
                if (category === 'Tất cả') {
                  count = courses.length;
                } else if (category === 'KIẾN THỨC CHUNG') {
                  count = courses.filter(c => categoryGroups['KIẾN THỨC CHUNG'].includes(c.category)).length;
                } else if (category === 'CHUYÊN MÔN NGHIỆP VỤ') {
                  count = courses.filter(c => categoryGroups['CHUYÊN MÔN NGHIỆP VỤ'].includes(c.category)).length;
                } else if (category === 'ĐÀO TẠO KHÁC') {
                  const allDefinedCategories = [
                    ...categoryGroups['KIẾN THỨC CHUNG'],
                    ...categoryGroups['CHUYÊN MÔN NGHIỆP VỤ']
                  ];
                  count = courses.filter(c => !allDefinedCategories.includes(c.category)).length;
                }
                
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                      selectedCategory === category
                        ? 'bg-white text-indigo-600 shadow-sm'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {category} ({count})
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Lọc theo loại hình đào tạo</h3>
            <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg">
              {trainingTypes.map((trainingType) => (
                <button
                  key={trainingType}
                  onClick={() => setSelectedTrainingType(trainingType)}
                  className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                    selectedTrainingType === trainingType
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {trainingType} ({trainingType === 'Tất cả' ? courses.length : courses.filter(c => c.trainingType === trainingType).length})
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Courses Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        {filteredCourses.length === 0 ? (
          <div className="text-center py-12">
            <svg className="h-16 w-16 mx-auto mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Không tìm thấy khóa học</h3>
            <p className="text-gray-500">Thử thay đổi bộ lọc để xem thêm khóa học.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" aria-label="Bảng danh sách khóa học">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Tên khóa học
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Danh mục
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Loại hình đào tạo
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Thời gian
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Chứng chỉ
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Trạng thái
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Hành động</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCourses.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-md bg-blue-100 text-blue-700">
                          <svg
                            className="h-6 w-6"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                            />
                          </svg>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            <Link 
                              to={`/lms/courses/${course.id}`}
                              className="hover:underline text-indigo-600"
                              aria-label={`Xem khóa học ${course.title}`}
                            >
                              {course.title}
                            </Link>
                          </div>
                          <div className="text-sm text-gray-500">{course.description.length > 60 ? `${course.description.substring(0, 60)}...` : course.description}</div>
                          <div className="text-xs text-gray-400">#{course.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {course.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTrainingTypeColor(course.trainingType)}`}>
                        {course.trainingType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>{course.duration}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {course.certification ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Có chứng chỉ
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          Không có
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(course.status)}`}>
                        {course.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link
                        to={`/lms/courses/${course.id}`}
                        className="text-indigo-600 hover:text-indigo-900"
                        aria-label={`Xem khóa học ${course.title}`}
                      >
                        Xem chi tiết
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CoursesPage; 